/**
 * @fileoverview Reset-password form, shown on a verified /reset-password link.
 * @description Client-side validated (via React Hook Form + buildResetPasswordFormSchema) form for
 * choosing a new password with a reset-link token: the same live policy checklist and
 * confirm-password match hint as the set-password and change-password forms (PasswordPolicyHints),
 * submitted via useResetPassword, which sends the visitor to /login on success. If the link stops
 * being usable between verifying and submitting (1031 — e.g. its attempt limit ran out on
 * rejected passwords), a "request a new link" action is offered alongside the error.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { type FormEvent } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import {
  PasswordMatchHint,
  PasswordPolicyChecklist,
} from '@/features/auth/components/PasswordPolicyHints'
import { usePasswordPolicy } from '@/features/auth/hooks/usePasswordPolicy'
import { useResetPassword } from '@/features/auth/hooks/useResetPassword'
import {
  buildResetPasswordFormSchema,
  type ResetPasswordFormValues,
} from '@/features/auth/schemas/reset-password-form.schema'
import Button from '@/shared/components/atoms/Button'
import LinkButton from '@/shared/components/atoms/LinkButton'
import PasswordField from '@/shared/components/molecules/PasswordField'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { ApiError } from '@/shared/types/http.types'

interface ResetPasswordFormProps {
  token: string
}

/**
 * @description Renders the reset request's error, followed by a "request a new link" action when
 * the error says the link itself has expired or been used.
 * @param {{ error: ApiError }} props - The reset mutation's error.
 */
function ErrorAlert({ error }: { error: ApiError }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const linkExpired = error.code === ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID

  return (
    <div
      role="alert"
      className="text-[14px] text-red-600"
      data-testid="reset-password-error-message"
    >
      <p>{error.message}</p>
      {linkExpired && (
        <div className="mt-2">
          <LinkButton
            size="sm"
            onClick={() => void navigate(ROUTES.FORGOT_PASSWORD)}
            data-testid="reset-password-request-new-link-link"
          >
            {t('resetPassword.requestNewLink')}
          </LinkButton>
        </div>
      )}
    </div>
  )
}

/**
 * @component
 * @param {ResetPasswordFormProps} props - The verified reset token.
 * @param {string} props.token - The token from the /reset-password URL.
 */
function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { t } = useTranslation()
  const resetPasswordMutation = useResetPassword()
  const { data: policy } = usePasswordPolicy()
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(buildResetPasswordFormSchema(policy)),
    defaultValues: { newPassword: '', confirmPassword: '' },
  })

  const newPassword = useWatch({ control, name: 'newPassword' })
  const confirmPassword = useWatch({ control, name: 'confirmPassword' })

  /**
   * @description Submits the reset-password mutation with the schema-validated passwords.
   * @param {ResetPasswordFormValues} values - The validated new/confirm passwords.
   */
  const onValid = (values: ResetPasswordFormValues) => {
    resetPasswordMutation.mutate({ token, ...values })
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid. Wrapped so the DOM
   * onSubmit handler returns void rather than the Promise React Hook Form's handleSubmit produces.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      className="mt-7 flex flex-col gap-[22px]"
      onSubmit={onSubmit}
      noValidate
      data-testid="reset-password-form"
    >
      <div>
        <PasswordField
          id="newPassword"
          data-testid="reset-password-new-password-input"
          label={t('acceptInvitation.newPasswordLabel')}
          placeholder={t('acceptInvitation.newPasswordPlaceholder')}
          autoComplete="new-password"
          error={errors.newPassword && t(errors.newPassword.message ?? '')}
          {...register('newPassword', { onChange: () => resetPasswordMutation.reset() })}
        />
        <PasswordPolicyChecklist
          policy={policy}
          value={newPassword}
          data-testid="reset-password-policy-checklist"
        />
      </div>

      <div>
        <PasswordField
          id="confirmPassword"
          data-testid="reset-password-confirm-password-input"
          label={t('acceptInvitation.confirmPasswordLabel')}
          placeholder={t('acceptInvitation.confirmPasswordPlaceholder')}
          autoComplete="new-password"
          error={errors.confirmPassword && t(errors.confirmPassword.message ?? '')}
          {...register('confirmPassword', { onChange: () => resetPasswordMutation.reset() })}
        />
        <PasswordMatchHint
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          data-testid="reset-password-match-hint"
        />
      </div>

      {resetPasswordMutation.isError && <ErrorAlert error={resetPasswordMutation.error} />}

      <Button
        type="submit"
        isLoading={resetPasswordMutation.isPending}
        data-testid="reset-password-submit-button"
      >
        {resetPasswordMutation.isPending
          ? t('resetPassword.submitPending')
          : t('resetPassword.submitIdle')}
      </Button>
    </form>
  )
}

export default ResetPasswordForm
